import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCalendarDay,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { getCityDualDisplayName, getTripDisplay } from "../../utils/tripDisplay";
import { isFreeDayTrip } from "../../utils/tourTripOptions";

const formattedDateString = (dateString, locale = "en-GB") =>
  new Date(dateString).toLocaleDateString(locale, {
    timeZone: "Europe/Sarajevo",
    day: "2-digit",
    month: "short",
  });

const TripCard = ({
  trip,
  onChangeTrip,
  dateLocale,
  dayNumber,
  language,
  isHighlighted,
}) => {
  const { t } = useTranslation("home");
  const display = getTripDisplay(trip, t, language);
  const freeDay = isFreeDayTrip(trip);
  const fromCity = getCityDualDisplayName(trip.startingPoint, t, language);
  const toCity = getCityDualDisplayName(trip.destination, t, language);

  return (
    <Card
      w="full"
      variant="outline"
      borderRadius="xl"
      borderColor={isHighlighted ? "teal.400" : "gray.100"}
      shadow={isHighlighted ? "md" : "sm"}
      transition="all 0.25s ease"
      cursor="pointer"
      role="button"
      tabIndex={0}
      onClick={onChangeTrip}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onChangeTrip();
        }
      }}
      _hover={{ shadow: "md", borderColor: "teal.300" }}
      _focusVisible={{ boxShadow: "outline" }}
    >
      <CardBody p={{ base: 4, md: 5 }}>
        <VStack align="stretch" spacing={4}>
          <Flex
            justify="space-between"
            align={{ base: "flex-start", sm: "center" }}
            flexDir={{ base: "column", sm: "row" }}
            gap={2}
          >
            <HStack spacing={2} flexWrap="wrap">
              {dayNumber != null && (
                <Badge
                  colorScheme={freeDay ? "gray" : "teal"}
                  variant="subtle"
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  <FontAwesomeIcon icon={faCalendarDay} size="xs" />
                  &nbsp;{t("tours.builder.dayLabel", { number: dayNumber })}
                </Badge>
              )}
              {trip.date && (
                <Text fontSize="sm" color="gray.500" fontWeight="medium">
                  {formattedDateString(trip.date, dateLocale)}
                </Text>
              )}
            </HStack>
            {!freeDay && trip.startingPoint && trip.destination && (
              <Badge
                variant="outline"
                colorScheme="gray"
                borderRadius="full"
                fontSize="xs"
              >
                {t("tours.tripCard.routeTo", { from: fromCity, to: toCity })}
              </Badge>
            )}
          </Flex>

          {freeDay ? (
            <Box>
              <Text color="gray.700" fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
                {t("tours.tripCard.freeActivityDay")}
              </Text>
              <Text fontSize="sm" color="gray.500" mt={1}>
                {t("tours.builder.freeDayHint")}
              </Text>
            </Box>
          ) : (
            <>
              <Text color="gray.700" lineHeight="tall" fontSize={{ base: "sm", md: "md" }}>
                {display.description}
              </Text>

              <Flex gap={2} flexWrap="wrap">
                {display.attractions.map((attraction, index) => (
                  <Tag
                    key={index}
                    size="sm"
                    variant="subtle"
                    colorScheme="teal"
                    borderRadius="full"
                  >
                    <FontAwesomeIcon icon={faLocationDot} size="xs" />
                    &nbsp;{attraction}
                  </Tag>
                ))}
              </Flex>
            </>
          )}

          <Flex justify="flex-end">
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              leftIcon={<FontAwesomeIcon icon={faArrowsRotate} />}
              onClick={(event) => {
                event.stopPropagation();
                onChangeTrip();
              }}
            >
              {t("tours.tripCard.changeTrip")}
            </Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default TripCard;
